import { Stack, Text } from "@mantine/core";
import { useAppSelector } from "app/hooks";
import { BSelect } from "components";
import {
  selectActiveProducts,
  selectProductCategories,
} from "features/admin/catalog/Catalog.slice";
import { forwardRef, ReactNode } from "react";
import { useTranslation } from "react-i18next";

export interface ProductSelectProps {
  required?: boolean;
  onChange: (id: string) => void;
  error?: ReactNode;
  value?: string; // could be undefined if form has no initial values
  disabled?: boolean;
  label?: string;
}

export const ProductSelect = (props: ProductSelectProps) => {
  const { t } = useTranslation();
  const categories = useAppSelector(selectProductCategories);
  const items = useAppSelector(selectActiveProducts);
  const data = items.map((o) => ({
    value: o.id,
    label: o.name,
    code: o.code,
    group: t(categories.find((c) => c.id === o.categoryId)?.name ?? "OTHER"),
  }));

  const onSelectChange = (value?: string) => {
    if (value) {
      const product = items.find((o) => o.id === value);
      if (product) {
        props.onChange(value);
      }
    }
  };

  return (
    <BSelect
      {...props}
      onChange={onSelectChange}
      label={props.label ?? t("PRODUCT")}
      itemComponent={SelectItem}
      filter={(value, item) =>
        item.label?.toLowerCase().includes(value.toLowerCase().trim()) ||
        item.code?.toLowerCase().includes(value.toLowerCase().trim())
      }
      nothingFound={t("NOTHING_FOUND")}
      data={data}
      limit={20}
    />
  );
};

interface ItemProps extends React.ComponentPropsWithoutRef<"div"> {
  label: string;
  code?: string;
}

const SelectItem = forwardRef<HTMLDivElement, ItemProps>(
  ({ code, label, ...others }: ItemProps, ref) => (
    <div ref={ref} {...others}>
      <Stack>
        <div>
          <Text>{label}</Text>
          {code && (
            <Text size="xs" color="dimmed">
              {code}
            </Text>
          )}
        </div>
      </Stack>
    </div>
  )
);
